import { readonly, ref } from "vue";
import * as defaultAPI from "@/api/admin/admin";
import {
  AdminV1OrganizationsPlayerDataByProfileIdRetrieveFormat,
  type AdminPlayerData,
  type PatchedAdminPlayerData,
} from "@/api/model";
import type {
  AdminV1OrganizationsPlayerDataByProfileIdRetrieveParams,
  PlayerDataErrorDetailGroup,
} from "@/api/model";

export type NonReadOnlyAdminPlayerData = Parameters<
  typeof defaultAPI.adminV1OrganizationsPlayerDataCreate
>[1];

export interface ErrorGroup {
  status: number;
  data: PlayerDataErrorDetailGroup;
  success: number;
}

export interface PlayerDataAPI {
  adminV1OrganizationsPlayerDataByProfileIdRetrieve: (
    organizationId: number,
    profileId: string,
    params?: AdminV1OrganizationsPlayerDataByProfileIdRetrieveParams,
    options?: Parameters<
      typeof defaultAPI.adminV1OrganizationsPlayerDataByProfileIdRetrieve
    >[3]
  ) => Promise<AdminPlayerData | Blob>;
  adminV1OrganizationsPlayerDataPartialUpdate: (
    organizationId: number,
    playerDataId: string,
    data: PatchedAdminPlayerData
  ) => Promise<AdminPlayerData>;
  adminV1OrganizationsPlayerDataCreate: (
    organizationId: number,
    data: NonReadOnlyAdminPlayerData
  ) => Promise<AdminPlayerData>;
}

interface Logger {
  error: (message: string, error: Error) => void;
}

export const usePlayerData = (
  api: PlayerDataAPI = defaultAPI,
  logger: Logger = console
) => {
  const datum = ref<AdminPlayerData | null>(null);
  const csvDatum = ref<Blob | null>(null);
  const isLoading = ref<boolean>(false);
  const isSaving = ref<boolean>(false);
  const fetchError = ref<Error | null>(null);
  const updateError = ref<Error | null>(null);
  const createError = ref<Error | null>(null);
  const fetchCsvError = ref<Error | null>(null);

  const _fetch = async (
    organizationId: number,
    profileId: number | string,
    format: AdminV1OrganizationsPlayerDataByProfileIdRetrieveFormat = AdminV1OrganizationsPlayerDataByProfileIdRetrieveFormat.json,
    options?: Parameters<
      typeof defaultAPI.adminV1OrganizationsPlayerDataByProfileIdRetrieve
    >[3]
  ): Promise<AdminPlayerData | Blob> => {
    isLoading.value = true;
    fetchError.value = null;
    createError.value = null;
    updateError.value = null;

    return api.adminV1OrganizationsPlayerDataByProfileIdRetrieve(
      organizationId,
      profileId.toString(),
      { format },
      options || {}
    );
  };

  const fetch = async (
    organizationId: number,
    profileId: number | string
  ): Promise<void> => {
    try {
      const response = await _fetch(
        organizationId,
        profileId,
        AdminV1OrganizationsPlayerDataByProfileIdRetrieveFormat.json
      );
      if (response instanceof Blob) {
        throw new Error("Unexpected Blob response when fetching JSON data");
      }
      datum.value = response;
    } catch (error) {
      fetchError.value = error as Error;
      logger.error("Failed to fetch player data", error as Error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchCsv = async (
    organizationId: number,
    profileId: number | string
  ): Promise<void> => {
    csvDatum.value = null;
    fetchCsvError.value = null;
    try {
      const response = await _fetch(
        organizationId,
        profileId,
        AdminV1OrganizationsPlayerDataByProfileIdRetrieveFormat.csv,
        {
          headers: { Accept: "text/csv, application/json, */*" },
          responseType: "blob",
        }
      );
      if (!(response instanceof Blob)) {
        const error = new Error(
          `Unexpected non-Blob response when fetching CSV data: ${JSON.stringify(response)}`
        );
        fetchCsvError.value = error;
        logger.error("Failed to fetch player data", error);
        return;
      }
      csvDatum.value = response;
    } catch (error) {
      fetchCsvError.value = error as Error;
      logger.error("Failed to fetch player data", error as Error);
    } finally {
      isLoading.value = false;
    }
  };

  const update = async (
    organizationId: number,
    playerDataId: string,
    data: PatchedAdminPlayerData
  ): Promise<boolean> => {
    isSaving.value = true;
    try {
      const response = await api.adminV1OrganizationsPlayerDataPartialUpdate(
        organizationId,
        playerDataId,
        data
      );
      datum.value = response;
      return true;
    } catch (error) {
      updateError.value = error as Error;
      return false;
    } finally {
      isSaving.value = false;
    }
  };

  const create = async (
    organizationId: number,
    data: NonReadOnlyAdminPlayerData
  ): Promise<boolean> => {
    isSaving.value = true;
    try {
      const response = await api.adminV1OrganizationsPlayerDataCreate(
        organizationId,
        data
      );
      datum.value = response;
      return true;
    } catch (error) {
      createError.value = error as Error;
      return false;
    } finally {
      isSaving.value = false;
    }
  };

  return {
    datum: datum,
    csvDatum: csvDatum,
    isLoading: readonly(isLoading),
    isSaving: readonly(isSaving),
    fetchError: readonly(fetchError),
    fetchCsvError: readonly(fetchCsvError),
    updateError: readonly(updateError),
    createError: readonly(createError),
    fetch,
    fetchCsv,
    update,
    create,
  };
};
